// ---------------------------------------------------------------------------
// Where VerifyDeceased data comes from and how a record is confirmed.
//
// Sophisticated buyers in this space ask about sourcing before they ask about
// price. Keeping the answer explicit and in one place means the marketing pages,
// the FAQ and any sales collateral all tell the same story.
//
// Only add a source here if legal has confirmed we are licensed or permitted to
// use it, and only describe what we actually do. Vague sourcing loses deals;
// overstated sourcing loses contracts.
// ---------------------------------------------------------------------------

export interface DataSource {
  icon: string;
  title: string;
  description: string;
  /** Short label describing how quickly this source typically surfaces a death. */
  cadence: string;
}

export interface ConfirmationStep {
  title: string;
  description: string;
}

export const DATA_SOURCES: DataSource[] = [
  {
    icon: "landmark",
    title: "State and county vital records",
    description:
      "Death registrations and registrar filings sourced from state and county vital records offices nationwide. These are the authoritative record of a death and anchor the date of death we return.",
    cadence: "Authoritative source of record"
  },
  {
    icon: "shield",
    title: "SSA Death Master File",
    description:
      "The Social Security Administration Death Master File, accessed through the certification program administered by NTIS for entities with a legitimate fraud prevention or claims need.",
    cadence: "Federal file, certified access"
  },
  {
    icon: "document",
    title: "Obituaries and funeral home notices",
    description:
      "Published obituaries and funeral home and mortuary notices are monitored continuously nationwide. These usually appear days before a record reaches any government file.",
    cadence: "Often the earliest signal"
  },
  {
    icon: "pin",
    title: "Cemetery and interment records",
    description:
      "Interment, memorial and cemetery records used to corroborate a death and to resolve identity when names are common or spelling varies.",
    cadence: "Corroborating source"
  },
  {
    icon: "gavel",
    title: "Probate and estate court filings",
    description:
      "Probate and estate administration filings confirm a death through the court record and help establish the last known residence of the decedent.",
    cadence: "Corroborating source"
  },
  {
    icon: "home",
    title: "LeadCruncher property and address data",
    description:
      "Our parent company LeadCruncher contributes nationwide property and address data. This is what lets us return a last known property and mailing address on a match, not just a date.",
    cadence: "Enrichment layer"
  }
];

export const CONFIRMATION_STEPS: ConfirmationStep[] = [
  {
    title: "Ingest continuously, not on a monthly batch",
    description:
      "Sources are collected on a rolling basis rather than waiting for a monthly or quarterly file drop. This is the single biggest reason our records surface faster than the industry norm."
  },
  {
    title: "Standardise and resolve the identity",
    description:
      "Names are normalised for nicknames, middle initials, suffixes and common spelling variations, then resolved against date of birth and location so two different people with the same name are not merged."
  },
  {
    title: "Corroborate before publishing",
    description:
      "A record is confirmed against supporting evidence before it enters the production database. Single unverified signals are held rather than published, because a false positive on a living consumer is the costliest error we can make."
  },
  {
    title: "Enrich with last known property data",
    description:
      "Confirmed records are enriched with the last known property and mailing address from LeadCruncher property data, and an age at death is calculated from the confirmed dates."
  },
  {
    title: "Score and publish weekly",
    description:
      "Every published record carries a match confidence score so your team can set the threshold that fits your risk tolerance. New confirmed records go live every week."
  }
];

/** Answers to the sourcing questions enterprise buyers ask in diligence. */
export const SOURCING_FAQS = [
  {
    question: "Where does VerifyDeceased data actually come from?",
    answer:
      "We combine state and county vital records, the Social Security Administration Death Master File accessed through the NTIS certification program, published obituaries and funeral home notices, cemetery and interment records, and probate and estate court filings. Confirmed records are then enriched with last known property and mailing address data from our parent company LeadCruncher."
  },
  {
    question: "Is this just the SSA Death Master File?",
    answer:
      "No. The Death Master File is one input, and on its own it is both incomplete and slow. Obituaries, funeral notices and vital records filings usually surface a death well before it reaches a federal file, which is why our records are typically available within a week rather than two to three months."
  },
  {
    question: "How do you avoid false positives on living consumers?",
    answer:
      "We corroborate a death against supporting evidence before the record is published, and we resolve identity using date of birth and location rather than name alone. Every match returns a confidence score so you can set your own threshold, and we recommend a manual review step before any irreversible action such as closing an account."
  },
  {
    question: "How often is the data refreshed?",
    answer:
      "Sources are ingested continuously and new confirmed records are published every week, often within seven days of a person passing away. Most providers in this market operate on a two to three month cycle."
  }
];
